import { useCallback, useEffect, useState } from 'react';

import { Sidebar } from './Sidebar';

import logo from '@/assets/logo.png';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleOverlayKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setSidebarOpen(false);
    }
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Sidebar transform:
  // Mobile: hidden (-100%) by default, visible (0) when open
  // Desktop: always visible (0)
  const sidebarTransform = isDesktop ? 'translateX(0)' : (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)');

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      {sidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={handleCloseSidebar}
          onKeyDown={handleOverlayKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}

      {/* Mobile header with logo */}
      <header className="fixed top-0 left-0 right-0 z-40 h-14 bg-background border-b border-border lg:hidden flex items-center px-4">
        <button
          onClick={handleToggleSidebar}
          aria-label="Toggle sidebar"
          className="flex items-center gap-3"
        >
          <img src={logo} alt="BeanGo" className="h-8 w-auto" />
        </button>
        {sidebarOpen && !isDesktop && (
          <button
            onClick={handleCloseSidebar}
            className="ml-auto p-2 rounded-lg hover:bg-muted"
            aria-label="Close sidebar"
          >
            <i className="bi bi-x-lg text-xl"></i>
          </button>
        )}
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 z-50 w-64 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        }`}
        style={{ background: 'var(--color-sidebar)', color: 'var(--color-sidebar-foreground)', transform: sidebarTransform }}
      >
        {/* Collapse toggle (desktop only) */}
        <button
          onClick={handleToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-6 w-6 h-6 rounded-full bg-card border border-border items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shadow-sm"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`bi ${sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'} text-xs`}></i>
        </button>

        <Sidebar collapsed={sidebarCollapsed} />
      </aside>

      {/* Main content */}
      <main
        className="flex-1 min-w-0 min-h-screen overflow-x-hidden lg:transition-all lg:duration-300"
        style={{
          background: 'var(--color-background)'
        }}
      >
        <div className={`w-full p-4 pt-20 lg:pt-8 lg:px-8 max-w-7xl mx-auto ${
          sidebarCollapsed ? 'lg:ml-[4rem]' : 'lg:ml-[16rem]'
        }`}>
          {children}
        </div>
      </main>
    </div>
  );
}